import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { API } from "../apiBase";
import { PROFESSIONS } from "../lib/professions";

/**
 * Commande MSSanté depuis OhMyVue (lien public, sans compte).
 * Hors PublicShell, non référencée. Style inspiré de l'Annuaire santé.
 * Backend : /api/mss-commande/annuaire/* (lot 1) et /api/mss-commande/* (lot 3).
 */

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const A = `${API}/mss-commande`;
const DOMAINES = [
  { d: "metaconnect.mssante.fr", sub: "Domaine partagé MetaConnect" },
  { d: "ohmyvue.mssante.fr", sub: "Domaine partagé OhMyVue" },
  { d: "opticiens.mssante.fr", sub: "Domaine partagé des opticiens" },
  { d: "optique.mssante.fr", sub: "Domaine partagé optique" },
];
const MAX_COLLAB = 10;
const SIRENS_GENERIQUES = ["111111111", "000000000", "999999999", "123456789"];
const prenomPropre = (x) => String(x || "").trim().toLowerCase()
  .replace(/(^|[\s\-'’])(\p{L})/gu, (m, a, b) => a + b.toUpperCase());
const LISTE_PROFESSIONS = ["Opticien-lunetier", "Monteur vendeur", ...PROFESSIONS]
  .filter((x, i, t) => t.findIndex((y) => y.toLowerCase() === x.toLowerCase()) === i);
const ETAPES = ["Votre adresse", "Votre structure", "Responsable et collaborateurs", "Validation"];
const RE_LOCAL = /^[a-z0-9](?:[a-z0-9.-]{0,62}[a-z0-9])?$/;
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const SUPPORT_EMAIL = process.env.REACT_APP_OMV_SUPPORT_EMAIL || "contact@ohmyvue.fr";
const EXEMPLE_LOCAL = "nom-de-votre-structure";
const DELAI_RENVOI_S = 60;

const TYPES_ID = [
  { groupe: "Carte CPS ou e-CPS", items: [{ v: "8", l: "Numéro RPPS" }] },
  { groupe: "Carte CPE ou CPA", items: [
    { v: "3", l: "Établissement FINESS" }, { v: "4", l: "Établissement SIREN" },
    { v: "5", l: "Établissement SIRET" }, { v: "6", l: "Cabinet RPPS" }, { v: "1", l: "Cabinet ADELI" },
  ] },
  { groupe: "Carte CPS étudiant ou e-CPS", items: [{ v: "9", l: "Étudiant" }] },
  { groupe: "Autres", items: [{ v: "0", l: "ADELI" }, { v: "@", l: "Email" }] },
];
const FORMATS = {
  "8": { re: /^\d{11}$/, ph: "00000000000" },
  "0": { re: /^\d{9}$/, ph: "000000000" },
  "1": { re: /^\d{9}\/[^\s/]{1,40}$/, ph: "000000000/matricule" },
  "3": { re: /^[0-9A-Za-z]{9}\/[^\s/]{1,40}$/, ph: "000000000/matricule" },
  "4": { re: /^\d{9}\/[^\s/]{1,40}$/, ph: "000000000/matricule" },
  "5": { re: /^\d{14}\/[^\s/]{1,40}$/, ph: "00000000000000/matricule" },
  "6": { re: /^\d{11}\/[^\s/]{1,40}$/, ph: "00000000000/matricule" },
  "9": { re: /^\d{11}$/, ph: "00000000000" },
  "@": { re: RE_EMAIL, ph: "email@email.fr" },
};
const CPE = (n, lib) => `Le numéro se situe sur la première ligne de la carte CPE ou CPA. Il est préfixé d'un ${n} et comporte un caractère slash « / ». Le code se compose du numéro ${lib} / Matricule (suite quelconque de caractères).`;
const AIDE = {
  "8": { t: "Numéro RPPS", img: "/cartes/carte-cps-ecps.jpg", txt: "Si vous disposez d'une carte CPS (Carte de Professionnel de Santé) délivrée par votre ordre professionnel (médecins, infirmiers, sages-femmes…), le numéro RPPS se situe sur la première ligne de la carte CPS. Si vous avez une carte virtuelle e-CPS sur votre smartphone (professions médicales, paramédicales et médico-sociales), le numéro RPPS est l'Identifiant. Le premier chiffre est toujours 8, suivi de 11 chiffres." },
  "3": { t: "Établissement FINESS", img: "/cartes/carte-cpe.jpg", txt: CPE(3, "FINESS (9 chiffres)") },
  "4": { t: "Établissement SIREN", img: "/cartes/carte-cpe.jpg", txt: CPE(4, "SIREN (9 chiffres)") },
  "5": { t: "Établissement SIRET", img: "/cartes/carte-cpe.jpg", txt: CPE(5, "SIRET (14 chiffres)") },
  "6": { t: "Cabinet RPPS", img: "/cartes/carte-cpe.jpg", txt: CPE(6, "RPPS du praticien (11 chiffres)") },
  "1": { t: "Cabinet ADELI", img: "/cartes/carte-cpe.jpg", txt: CPE(1, "ADELI du praticien (9 chiffres)") },
  "9": { t: "Étudiant", img: "/cartes/carte-cps-ecps.jpg", txt: "Le premier chiffre est toujours 9, suivi de 11 chiffres. Numéro situé sur la première ligne de la carte CPS Étudiant ou « Identifiant » sur l'application e-CPS." },
  "0": { t: "ADELI", txt: "Le numéro ADELI se compose de 9 chiffres." },
  "@": { t: "Adresse email (non MSS)", txt: "Format de type email : xxx@xxx.xx" },
};
const CARTE = { valide: ["ok", "CPS valide"], opposition: ["warn", "CPS en opposition"], expiree: ["warn", "CPS expirée"], absente: ["n", "Pas de carte physique"], inconnue: ["n", "Carte non vérifiée"], cpe: ["n", "CPE / CPA"], sans: ["n", "Sans carte"] };
const BROUILLON = "omv-commande-mss";

const fmtDuree = (s) => `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")} s`;
const lireBrouillon = () => { try { return JSON.parse(sessionStorage.getItem(BROUILLON) || "null"); } catch (e) { return null; } };
const detail = (ex, defaut) => {
  const d = ex?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (ex?.response?.status === 429) return "Trop de tentatives. Patientez un moment puis réessayez.";
  return defaut || "Une erreur technique est survenue. Réessayez dans quelques instants.";
};

/* ------------------------------------------------------------------ petits composants */
function IcoStructure() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      <rect x="6" y="10" width="28" height="26" rx="2" fill="#E9EEF7" stroke="#2F4B7C" strokeWidth="1.4" />
      <rect x="15" y="3" width="10" height="9" rx="1.5" fill="#1F6FB8" />
      <path d="M20 5v5M17.5 7.5h5" stroke="#fff" strokeWidth="1.4" />
      <g fill="#2F4B7C"><rect x="10" y="15" width="4" height="4" /><rect x="18" y="15" width="4" height="4" /><rect x="26" y="15" width="4" height="4" /><rect x="10" y="22" width="4" height="4" /><rect x="26" y="22" width="4" height="4" /></g>
      <rect x="17" y="27" width="6" height="9" fill="#2F4B7C" />
      <circle cx="32" cy="32" r="6" fill="#fff" stroke="#8A2A4A" strokeWidth="1.4" />
      <path d="m29.5 32 2 2 3-3.5" stroke="#8A2A4A" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

function CarteAnnuaire({ s, typeLabel, action, sel }) {
  const idLabel = s.type_id === "FINESS" ? "FINESS" : s.type_id;
  return (
    <article className={`omv-as-card${sel ? " sel" : ""}`}>
      <div className="omv-as-top">
        <span className="omv-as-ico"><IcoStructure /></span>
        <div>
          <div className="omv-as-name">{s.nom || "Nom non communiqué"}</div>
          <div className="omv-as-id">Identifiant {idLabel} :<span>{s.id}</span></div>
          <div className="omv-as-t">{typeLabel}</div>
          {s.adresse && <div className="omv-as-t">{s.adresse}</div>}
          {s.boites_nos_domaines?.length > 0 && <div className="omv-as-t">Boîte publiée : {s.boites_nos_domaines.join(", ")}</div>}
        </div>
      </div>
      <div className="omv-as-bot">
        <span className={`omv-pill${s.ouverte ? "" : " closed"}`}>{s.ouverte ? "✓" : "✕"}&nbsp; Structure {s.ouverte ? "ouverte" : "fermée"}</span>
        {action}
      </div>
    </article>
  );
}

function Modale({ titre, tag, onClose, children, pied }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="omv-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="omv-modal" role="dialog" aria-modal="true" aria-label={titre}>
        <header>
          <span className="omv-mi">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 21V3h10v4h8v14H3Z" /></svg>
          </span>
          <h3>{titre}</h3>
          {tag && <span className="omv-tag">{tag}</span>}
          <button type="button" className="omv-x" onClick={onClose} aria-label="Fermer">×</button>
        </header>
        <div className="omv-body">{children}</div>
        <footer>{pied}</footer>
      </div>
    </div>
  );
}

const BtnFleche = ({ children, ...p }) => (
  <button type="button" className="omv-btn" {...p}>{children}<i aria-hidden="true">→</i></button>
);

/* ------------------------------------------------------------------ page */
export default function OhMyVueCommandeMss() {
  const init = useMemo(lireBrouillon, []);
  const debut = useRef(init?.debut || Date.now());
  const [etape, setEtape] = useState(init?.etape && init.etape < 5 ? init.etape : 1);
  const [maxEtape, setMaxEtape] = useState(init?.maxEtape || 1);
  const [local, setLocal] = useState(init?.local ?? "");
  const [domaine, setDomaine] = useState(init?.domaine || "metaconnect.mssante.fr");
  const [sousDomaine, setSousDomaine] = useState(init?.sousDomaine || "");
  const [description, setDescription] = useState(init?.description || "");
  const [listeRouge, setListeRouge] = useState(!!init?.listeRouge);
  const [dispo, setDispo] = useState({});
  const [dispoEssai, setDispoEssai] = useState(0);
  const [dedie, setDedie] = useState({ etat: "vide" });
  const [ej, setEj] = useState(init?.ej || null);
  const [eg, setEg] = useState(init?.eg || null);
  const [sousOnglet, setSousOnglet] = useState("ej");
  const [modale, setModale] = useState(null);
  const [personnes, setPersonnes] = useState((init?.personnes || []).map((p) => ({ ...p, fichier: null })));
  const [emailAbo, setEmailAbo] = useState(init?.emailAbo || "");
  const [habil, setHabil] = useState(false);
  const [optionOk, setOptionOk] = useState(false);
  const [siteWeb, setSiteWeb] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [envoi, setEnvoi] = useState({ etat: "", msg: "" });
  const [jeton, setJeton] = useState(null);
  const [code, setCode] = useState("");
  const [resultat, setResultat] = useState(null);
  const [horloge, setHorloge] = useState(Date.now());
  const [renvoiApres, setRenvoiApres] = useState(0);
  const [quitterCode, setQuitterCode] = useState(false);

  // brouillon (onglet uniquement, sans les fichiers)
  useEffect(() => {
    try {
      if (etape >= 5) { sessionStorage.removeItem(BROUILLON); return; }
      sessionStorage.setItem(BROUILLON, JSON.stringify({
        debut: debut.current, etape, maxEtape, local, domaine, sousDomaine, description, listeRouge, ej, eg, emailAbo,
        // eslint-disable-next-line no-unused-vars
        personnes: personnes.map(({ fichier, ...p }) => p),
      }));
    } catch (e) { /* stockage indisponible */ }
  }, [etape, maxEtape, local, domaine, sousDomaine, description, listeRouge, ej, eg, emailAbo, personnes]);

  const aller = (n) => { setEtape(n); setMaxEtape((m) => Math.max(m, n)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const localOk = RE_LOCAL.test(local) && !local.includes("..");
  const sdOk = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/.test(sousDomaine);
  const adresse = `${local || EXEMPLE_LOCAL}@${domaine === "dedie" ? `${sousDomaine || "votre-nom"}.mssante.fr` : domaine}`;

  /* --- étape 1 : disponibilités --- */
  useEffect(() => {
    if (!localOk) { setDispo({}); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${A}/disponibilite`, { params: { local, dedie: sdOk ? sousDomaine : "" } });
        setDispo({ ...data.domaines, dedie: data.dedie?.adresse_libre });
      } catch (e) { setDispo({ _erreur: true }); }
    }, 350);
    return () => clearTimeout(t);
  }, [local, sousDomaine, localOk, sdOk, dispoEssai]);

  // si le domaine coché est déjà pris, on bascule sur le premier domaine partagé libre
  useEffect(() => {
    if (domaine === "dedie" || dispo[domaine] !== false) return;
    const libre = DOMAINES.find((o) => dispo[o.d] === true);
    if (libre) setDomaine(libre.d);
  }, [dispo, domaine]);

  useEffect(() => {
    if (!sousDomaine) { setDedie({ etat: "vide" }); return undefined; }
    if (!sdOk) { setDedie({ etat: "invalide" }); return undefined; }
    setDedie({ etat: "verif" });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/mssante/check-domain`, { name: sousDomaine });
        setDedie({ etat: data.available ? "libre" : (data.cache_loaded ? "pris" : "inconnu") });
      } catch (e) { setDedie({ etat: "inconnu" }); }
    }, 500);
    return () => clearTimeout(t);
  }, [sousDomaine, sdOk]);

  const etatDomaine = (d) => {
    if (!local) return ["n", "Saisissez un nom"];
    if (!localOk) return ["crit", "Format invalide"];
    if (dispo._erreur) return ["warn", "Vérification impossible"];
    if (d === "dedie") {
      if (dedie.etat === "vide") return ["n", "Saisissez un nom"];
      if (dedie.etat === "invalide") return ["crit", "Sous-domaine invalide"];
      if (dedie.etat === "verif") return ["n", "Vérification…"];
      if (dedie.etat === "pris") return ["crit", "Sous-domaine déjà pris"];
      if (dispo.dedie === false) return ["crit", "Adresse déjà utilisée"];
      if (dedie.etat === "inconnu") return ["warn", "À confirmer par nos équipes"];
      return ["ok", "Disponible · liste blanche ANS"];
    }
    if (dispo[d] === undefined) return ["n", "Vérification…"];
    return dispo[d] ? ["ok", "Disponible"] : ["crit", "Déjà utilisée"];
  };
  const domaineChoisiOk = domaine === "dedie"
    ? localOk && sdOk && ["libre", "inconnu"].includes(dedie.etat) && dispo.dedie !== false
    : localOk && dispo[domaine] === true;

  /* --- étape 3 : personnes --- */
  const resp = personnes.find((p) => p.role === "responsable");
  const nbCollab = personnes.filter((p) => p.role === "collaborateur").length;
  const incomplets = personnes.filter((p) => !p.profession || (p.role === "responsable" && !RE_EMAIL.test(p.email || "")));
  const enregistrerPersonne = (p, index) => {
    setPersonnes((liste) => {
      if (p.role === "responsable") {
        const autres = liste.filter((x) => x.role !== "responsable" && !(x.type_id === p.type_id && x.identifiant === p.identifiant));
        const ancien = index == null ? liste.find((x) => x.role === "responsable") : null;
        const garde = ancien && !(ancien.type_id === p.type_id && ancien.identifiant === p.identifiant)
          ? [{ ...ancien, role: "collaborateur", droit: "Délégataire" }] : [];
        return [p, ...autres, ...garde];
      }
      if (index != null) return liste.map((x, i) => (i === index ? p : x));
      return [...liste, p];
    });
    setModale(null);
  };

  const nommerResponsable = (i) => {
    const cible = personnes[i];
    const nouveau = { ...cible, role: "responsable", droit: "Responsable", email: cible.email || emailAbo || "" };
    setPersonnes((liste) => [
      nouveau,
      ...liste.filter((_, j) => j !== i).map((x) => (x.role === "responsable" ? { ...x, role: "collaborateur", droit: "Délégataire" } : x)),
    ]);
    if (!RE_EMAIL.test(nouveau.email) || !nouveau.profession) {
      setModale({ type: "personne", role: "responsable", index: 0, valeur: nouveau });
    }
  };

  const rattacher = (choisis, rppsResp) => {
    const nouveaux = choisis.map((x) => {
      const estResp = !resp && x.rpps === rppsResp;
      return {
        role: estResp ? "responsable" : "collaborateur", nom: x.nom, prenom: prenomPropre(x.prenom), type_id: "8",
        identifiant: x.rpps, email: estResp ? emailAbo || "" : "", telephone: "",
        profession: professionConnue(x.profession), droit: estResp ? "Responsable" : "Délégataire",
        carte: x.carte || "inconnue", fichier: null, pieceNom: "", source: "annuaire",
      };
    });
    const nvResp = nouveaux.find((x) => x.role === "responsable");
    setPersonnes((liste) => {
      const deja = new Set(liste.map((x) => `${x.type_id}-${x.identifiant}`));
      const ajout = nouveaux.filter((x) => !deja.has(`8-${x.identifiant}`));
      const r = ajout.filter((x) => x.role === "responsable");
      return [...r, ...liste, ...ajout.filter((x) => x.role !== "responsable")];
    });
    if (nvResp && (!nvResp.email || !nvResp.profession)) {
      setModale({ type: "personne", role: "responsable", index: 0, valeur: nvResp });
    }
  };

  /* --- envoi --- */
  const envoyerCode = async () => {
    setEnvoi({ etat: "encours", msg: "" });
    const fichiers = [];
    const pers = personnes.map((p) => {
      const { fichier, ...reste } = p;
      let piece_index = null;
      if (fichier) { piece_index = fichiers.length; fichiers.push(fichier); }
      return { ...reste, piece_index };
    });
    const demande = {
      local, domaine, sous_domaine: domaine === "dedie" ? sousDomaine : "", description, liste_rouge: listeRouge,
      entite_juridique: { type_id: ej.type_id, identifiant: ej.id, nom: ej.nom || "", adresse: "", fhir_ids: ej.fhir_ids || [], verifiee: !!ej.verifiee },
      etablissement: { type_id: eg.type_id, identifiant: eg.id, nom: eg.nom || "", adresse: eg.adresse || "", fhir_ids: eg.fhir_ids || [], verifiee: !!eg.verifiee },
      personnes: pers, email_abonnement: emailAbo.trim(), habilitation: habil,
      option_dedie_acceptee: domaine === "dedie" && optionOk,
      turnstile_token: tsToken, site_web: siteWeb, debut_saisie_ms: debut.current,
    };
    const fd = new FormData();
    fd.append("demande", JSON.stringify(demande));
    fichiers.forEach((f) => fd.append("fichiers", f));
    try {
      const { data } = await axios.post(`${A}/code`, fd);
      setJeton({ ...data, expire: Date.now() + (data.validite_min || 15) * 60000 });
      setRenvoiApres(Date.now() + DELAI_RENVOI_S * 1000); setQuitterCode(false);
      setCode(""); setEnvoi({ etat: "", msg: "" });
      setTsToken(""); try { window.turnstile?.reset?.(); } catch (e) { /* jeton à usage unique */ }
      aller(5);
    } catch (ex) {
      setEnvoi({ etat: "erreur", msg: detail(ex) });
      setTsToken(""); try { window.turnstile?.reset?.(); } catch (e) { /* widget déjà retiré */ }
    }
  };
  useEffect(() => {
    if (etape !== 5) return undefined;
    const t = setInterval(() => setHorloge(Date.now()), 1000);
    return () => clearInterval(t);
  }, [etape]);

  const confirmer = async (valeur = code) => {
    if (envoi.etat === "encours") return;
    setEnvoi({ etat: "encours", msg: "" });
    try {
      const { data } = await axios.post(`${A}/confirmer`, { jeton: jeton.jeton, code: valeur });
      setResultat(data); setEnvoi({ etat: "", msg: "" }); setEtape(6);
      try { sessionStorage.removeItem(BROUILLON); } catch (e) { /* ignore */ }
    } catch (ex) {
      const r = ex?.response?.data;
      setEnvoi({ etat: "erreur", msg: detail(ex) + (r?.essais_restants != null ? ` Essais restants : ${r.essais_restants}.` : "") });
    }
  };
  const renvoyer = async () => {
    try {
      const { data } = await axios.post(`${A}/renvoyer`, { jeton: jeton.jeton });
      setJeton((j) => ({ ...j, expire: Date.now() + (j.validite_min || 15) * 60000 }));
      setRenvoiApres(Date.now() + DELAI_RENVOI_S * 1000); setCode("");
      setEnvoi({ etat: "info", msg: `Nouveau code envoyé. Renvois restants : ${data.renvois_restants}.` });
    } catch (ex) { setEnvoi({ etat: "erreur", msg: detail(ex) }); }
  };

  const resteCode = jeton?.expire ? Math.max(0, Math.round((jeton.expire - horloge) / 1000)) : 0;
  const expire = !!jeton?.expire && resteCode === 0;
  const resteRenvoi = Math.max(0, Math.ceil((renvoiApres - horloge) / 1000));

  const peutValider = habil && RE_EMAIL.test(emailAbo.trim()) && (domaine !== "dedie" || optionOk)
    && (!TURNSTILE_SITE_KEY || tsToken) && envoi.etat !== "encours";

  /* ------------------------------------------------------------------ rendu */
  return (
    <div className="omv-page" data-testid="page-ohmyvue-commande-mss">
      <Helmet>
        <title>Commander sa messagerie de santé sécurisée · OhMyVue</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Jost:wght@400;500;600&display=swap" />
      </Helmet>
      <style>{CSS}</style>

      <div className="omv-appbar">
        <span className="omv-logo"><img src="/logos/ohmyvue.png" alt="OhMyVue" /></span>
        <span className="omv-who">Commander sa messagerie de santé sécurisée</span>
        <a className="omv-aide" href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Aide commande messagerie de santé")}`}>Besoin d'aide ?</a>
      </div>

      {etape <= 4 && (
        <nav className="omv-flow" aria-label="Étapes">
          {ETAPES.map((l, i) => {
            const n = i + 1;
            return (
              <button key={l} type="button" disabled={n > maxEtape} onClick={() => aller(n)}
                aria-current={n === etape ? "step" : undefined} className={n < etape ? "done" : ""}>
                <span className="n">{n}</span>{l}
              </button>
            );
          })}
        </nav>
      )}

      <main className="omv-main">
        {/* ============ ÉTAPE 1 ============ */}
        {etape === 1 && (
          <section className="omv-screen omv-card1">
            <div className="omv-head">
              <h1>Choisissez votre adresse MSSanté</h1>
              <p>Le début de l'adresse, puis le domaine. Les domaines partagés sont inclus dans votre abonnement ; le sous-domaine à votre nom est une option.</p>
            </div>
            <ul className="omv-trust">
              <li>Hébergement certifié HDS en France</li>
              <li>Espace de Confiance MSSanté de l'ANS</li>
              <li>Utilisateurs illimités sur la boîte</li>
            </ul>
            {maxEtape === 1 && (
              <details className="omv-avant">
                <summary>Ce qu'il faut prévoir (environ 5 minutes)</summary>
                <ul>
                  <li>le SIREN de votre société et le SIRET de l'établissement (ou leurs numéros FINESS) ;</li>
                  <li>le numéro RPPS, ou la carte CPS / CPE, de chaque personne qui utilisera la boîte ;</li>
                  <li>l'adresse e-mail de votre abonnement OhMyVue, pour recevoir le code de confirmation.</li>
                </ul>
              </details>
            )}
            <div className="omv-typer">
              <label htmlFor="omv-local">Avant le @</label>
              <div className="omv-big">
                <input id="omv-local" data-testid="omv-local" value={local} autoComplete="off" spellCheck="false"
                  placeholder={EXEMPLE_LOCAL} aria-describedby="omv-local-aide"
                  onChange={(e) => setLocal(e.target.value.toLowerCase().replace(/\s/g, ""))} />
                <span>@</span>
              </div>
              <small id="omv-local-aide" className={!local || localOk ? "" : "err"}>Le nom de votre structure, en minuscules : lettres, chiffres, point et tiret. Exemples : optique-jargeau, vision-plus.orleans, accueil.optique-martin.</small>
            </div>
            <div className="omv-domsel">
              <label htmlFor="omv-dom">Domaine</label>
              <select id="omv-dom" value={domaine} onChange={(e) => setDomaine(e.target.value)}>
                {DOMAINES.map((o) => {
                  const pris = localOk && dispo[o.d] === false;
                  return (
                    <option key={o.d} value={o.d} disabled={pris}>
                      {`@${o.d} — ${o.sub}${pris ? " · adresse déjà utilisée" : " · inclus"}`}
                    </option>
                  );
                })}
                <option value="dedie">@votre-nom.mssante.fr — à votre nom, option payante</option>
              </select>
            </div>
            <div className="omv-apercu">
              <span className="omv-mail"><span className={`l${local ? "" : " ph"}`}>{local || EXEMPLE_LOCAL}</span>@{domaine === "dedie" ? `${sousDomaine || "votre-nom"}.mssante.fr` : domaine}</span>
              {(() => { const [c, l] = etatDomaine(domaine); return <span className={`omv-chip ${c}`}>{l}</span>; })()}
            </div>
            {domaine === "dedie" && (
              <div className="omv-dedbox">
                <span className="omv-dedin">@<input aria-label="Sous-domaine dédié" value={sousDomaine} placeholder="votre-nom"
                  onChange={(e) => setSousDomaine(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} />.mssante.fr</span>
                <span className="omv-pr">Option : 1 500 € HT<br /><em>ou 12 × 125 € HT · offre de lancement · TVA en sus</em></span>
              </div>
            )}
            {dispo._erreur && (
              <div className="omv-note warn" role="status">
                <span>La disponibilité des adresses n'a pas pu être vérifiée (connexion interrompue ?).{" "}
                  <button type="button" className="omv-link" onClick={() => { setDispo({}); setDispoEssai((n) => n + 1); }}>Réessayer</button></span>
              </div>
            )}
            <label className="omv-f">Description de la boîte
              <input value={description} maxLength={400} onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex. : boîte du magasin, échanges avec les prescripteurs" />
            </label>
            <label className="omv-check"><input type="checkbox" checked={listeRouge} onChange={(e) => setListeRouge(e.target.checked)} />
              <span><strong>Liste rouge</strong> — ne pas afficher cette boîte dans l'Annuaire santé. Par défaut, elle y est publiée pour que les professionnels puissent vous écrire.</span></label>
            <div className="omv-actions"><span />
              <button type="button" className="omv-cta" disabled={!domaineChoisiOk || description.trim().length < 2} onClick={() => aller(2)}>Continuer : ma structure</button>
            </div>
            {(!domaineChoisiOk || description.trim().length < 2) && (
              <p className="omv-hint">Pour continuer : {[
                !local && "saisissez le début de l'adresse",
                local && !domaineChoisiOk && "choisissez une adresse disponible",
                description.trim().length < 2 && "ajoutez une courte description",
              ].filter(Boolean).join(", ")}.</p>
            )}
          </section>
        )}

        {/* ============ ÉTAPE 2 ============ */}
        {etape === 2 && (
          <section className="omv-screen">
            <div className="omv-head">
              <h1>Créez votre structure</h1>
              <p>Saisissez le SIREN ou le FINESS de votre entité juridique, puis choisissez votre établissement. Chaque identifiant est vérifié dans l'Annuaire santé : une structure introuvable ou fermée ne peut pas être ajoutée.</p>
            </div>
            <div className="omv-mc">
              <div className="omv-mc-tabs"><span className="on">Structures</span><span>Utilisateurs</span><span>Boîtes aux lettres</span></div>
              <div className="omv-panel">
                <div className="omv-row">
                  <button type="button" className={`omv-sub-tab${sousOnglet === "ej" ? " on" : ""}`} onClick={() => setSousOnglet("ej")}>Entités juridiques {ej && <small>(1)</small>}</button>
                  <button type="button" className={`omv-sub-tab${sousOnglet === "eg" ? " on" : ""}`} onClick={() => setSousOnglet("eg")}>Établissements</button>
                  <span className="omv-spacer" />
                  <BtnFleche data-testid="omv-add-struct" onClick={() => {
                    if (!ej) { setSousOnglet("ej"); setModale({ type: "ej" }); } else { setSousOnglet("eg"); setModale({ type: "eg" }); }
                  }}>{!ej ? "Ajouter une entité juridique" : eg ? "Changer d'établissement" : "Ajouter un établissement"}</BtnFleche>
                </div>
                <div className="omv-guide">
                  {!ej ? <><b>1 / 2</b> Ajoutez votre entité juridique avec son SIREN ou son FINESS juridique.</>
                    : !eg ? <><b>2 / 2</b> Ajoutez l'établissement qui portera la boîte.</>
                      : <><b>✓</b> Structure prête : {eg.nom || ej.nom} · {eg.adresse || eg.id}</>}
                </div>
                <div className="omv-tbl">
                  {sousOnglet === "ej" ? (
                    <table><thead><tr><th>Nom</th><th>FINESS</th><th>SIREN</th><th /></tr></thead>
                      <tbody>{ej ? (
                        <tr><td>{ej.nom}{!ej.verifiee && <span className="omv-chip warn"> à vérifier</span>}</td>
                          <td>{ej.type_id === "FINESS" ? ej.id : ""}</td><td>{ej.type_id === "SIREN" ? ej.id : ""}</td>
                          <td><button type="button" className="omv-link" onClick={() => { setEj(null); setEg(null); }}>Retirer</button></td></tr>
                      ) : <tr><td colSpan={4} className="omv-empty">Aucune entité juridique. Cliquez sur « Ajouter ».</td></tr>}</tbody></table>
                  ) : (
                    <table><thead><tr><th>Nom</th><th>FINESS</th><th>SIRET</th><th /></tr></thead>
                      <tbody>{!ej ? <tr><td colSpan={4} className="omv-empty">Sélectionnez d'abord une entité juridique</td></tr>
                        : eg ? (
                          <tr><td>{eg.nom}<span className="omv-em">{eg.adresse}</span>{!eg.verifiee && <span className="omv-chip warn">à vérifier</span>}</td>
                            <td>{eg.type_id === "FINESS" ? eg.id : ""}</td><td>{eg.type_id === "SIRET" ? eg.id : ""}</td>
                            <td><button type="button" className="omv-link" onClick={() => setEg(null)}>Retirer</button></td></tr>
                        ) : <tr><td colSpan={4} className="omv-empty">Aucun établissement. Cliquez sur « Ajouter ».</td></tr>}</tbody></table>
                  )}
                </div>
              </div>
            </div>
            <div className="omv-actions">
              <button type="button" className="omv-ghost" onClick={() => aller(1)}>Retour</button>
              <button type="button" className="omv-cta" disabled={!(ej && eg)} onClick={() => aller(3)}>Responsable et collaborateurs</button>
            </div>
          </section>
        )}

        {/* ============ ÉTAPE 3 ============ */}
        {etape === 3 && (
          <section className="omv-screen">
            <div className="omv-head">
              <h1>Responsable de la boîte et collaborateurs</h1>
              <p>Commencez par le responsable, puis ajoutez jusqu'à {MAX_COLLAB} collaborateurs. Chacun se connectera avec sa carte CPS, sa carte CPE ou son e-CPS : l'aide « Où trouver ce numéro ? » montre où lire l'identifiant.</p>
            </div>
            <div className="omv-mc">
              <div className="omv-mc-tabs"><span>Structures</span><span className="on">Utilisateurs</span><span>Boîtes aux lettres</span></div>
              <div className="omv-panel">
                <div className="omv-row">
                  <span className="omv-sub-tab on">Utilisateurs <small>({personnes.length})</small></span>
                  <span className="omv-spacer" />
                  <span className={`omv-chip ${nbCollab >= MAX_COLLAB ? "warn" : "n"}`}>{nbCollab} / {MAX_COLLAB} collaborateurs</span>
                  {!resp && <BtnFleche data-testid="omv-add-resp" onClick={() => setModale({ type: "personne", role: "responsable" })}>Désigner le responsable</BtnFleche>}
                  {resp && <button type="button" className="omv-link" onClick={() => setModale({ type: "personne", role: "responsable" })}>Changer de responsable</button>}
                  <BtnFleche data-testid="omv-add-user" disabled={!resp || nbCollab >= MAX_COLLAB}
                    onClick={() => setModale({ type: "personne", role: "collaborateur" })}>Ajouter un collaborateur</BtnFleche>
                </div>
                {eg?.fhir_ids?.length > 0 && (
                  <ProfessionnelsAnnuaire eg={eg} personnes={personnes} aResp={!!resp}
                    placesLibres={MAX_COLLAB - nbCollab} onRattacher={rattacher} />
                )}
                <div className="omv-tbl">
                  <table>
                    <thead><tr><th>Nom</th><th>Identifiant</th><th>Profession</th><th>Carte</th><th>Justificatif</th><th>Droit sur la boîte</th><th /></tr></thead>
                    <tbody>
                      {personnes.length === 0 && <tr><td colSpan={7} className="omv-empty">Commencez par désigner le responsable de la boîte.</td></tr>}
                      {personnes.map((p, i) => {
                        const c = CARTE[p.carte] || CARTE.sans;
                        return (
                          <tr key={`${p.type_id}-${p.identifiant}`}>
                            <td>{p.nom} {p.prenom}<span className="omv-em">{p.email}</span></td>
                            <td className="omv-mono">{p.type_id === "@" ? "" : p.type_id}{p.identifiant}</td>
                            <td>{p.profession}</td>
                            <td><span className={`omv-chip ${c[0]}`}>{c[1]}</span></td>
                            <td>{p.fichier ? <span className="omv-chip ok">{p.fichier.name}</span> : p.pieceNom ? <span className="omv-chip warn">à rejoindre</span> : "—"}</td>
                            <td>{p.role === "responsable" ? <strong>Responsable</strong> : (
                              <select className="omv-select" value={p.droit} aria-label={`Droit de ${p.prenom} ${p.nom}`}
                                onChange={(e) => setPersonnes((l) => l.map((x, j) => (j === i ? { ...x, droit: e.target.value } : x)))}>
                                <option>Délégataire</option><option>Cotitulaire</option>
                              </select>)}</td>
                            <td className="omv-nowrap">
                              <button type="button" className="omv-link" onClick={() => setModale({ type: "personne", role: p.role, index: i, valeur: p })}>Modifier</button>
                              {p.role !== "responsable" && <button type="button" className="omv-link" onClick={() => nommerResponsable(i)}>Nommer responsable</button>}
                              <button type="button" className="omv-link" onClick={() => setPersonnes((l) => l.filter((_, j) => j !== i))}>Retirer</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {nbCollab > MAX_COLLAB && (
              <div className="omv-note warn" role="status">{MAX_COLLAB} collaborateurs maximum : retirez-en {nbCollab - MAX_COLLAB} pour continuer.</div>
            )}
            {incomplets.length > 0 && (
              <div className="omv-note warn" role="status">
                À compléter avant de continuer (bouton « Modifier ») : {incomplets.map((p) => `${p.prenom} ${p.nom}`).join(", ")} — {incomplets.some((p) => !p.profession) ? "profession manquante" : "e-mail du responsable manquant"}.
              </div>
            )}
            <div className="omv-actions">
              <button type="button" className="omv-ghost" onClick={() => aller(2)}>Retour</button>
              <button type="button" className="omv-cta" disabled={!resp || incomplets.length > 0 || nbCollab > MAX_COLLAB} onClick={() => { if (!emailAbo && resp?.email) setEmailAbo(resp.email); aller(4); }}>Vérifier et valider</button>
            </div>
          </section>
        )}

        {/* ============ ÉTAPE 4 ============ */}
        {etape === 4 && (
          <section className="omv-screen">
            <div className="omv-head">
              <h1>Vérifiez et validez</h1>
              <p>Votre boîte telle qu'elle sera créée. Elle est comprise dans l'abonnement que vous avez déjà souscrit.</p>
            </div>
            <div className="omv-mc">
              <div className="omv-mc-tabs"><span>Structures</span><span>Utilisateurs</span><span className="on">Boîtes aux lettres</span></div>
              <div className="omv-panel">
                <div className="omv-tbl"><table>
                  <thead><tr><th>État</th><th>Type de BAL</th><th>Email</th><th>ID EJ</th><th>Libellé EJ</th><th>ID EG</th><th>Libellé EG</th></tr></thead>
                  <tbody><tr><td><span className="omv-chip warn">À créer</span></td><td>Organisationnelle</td><td className="omv-mono">{adresse}</td>
                    <td className="omv-mono">{ej?.id}</td><td>{ej?.nom}</td><td className="omv-mono">{eg?.id}</td><td>{eg?.nom || eg?.adresse}</td></tr></tbody>
                </table></div>
              </div>
            </div>
            <div className="omv-mc">
              <div className="omv-panel">
                <div className="omv-row">
                  <span className="omv-sub-tab on">Utilisateurs <small>({personnes.length})</small></span>
                  <span className="omv-spacer" />
                  <button type="button" className="omv-link" onClick={() => aller(3)}>Modifier les utilisateurs</button>
                </div>
                <div className="omv-tbl"><table>
                  <thead><tr><th>Nom</th><th>Identifiant</th><th>Profession</th><th>E-mail</th><th>Droit</th></tr></thead>
                  <tbody>{personnes.map((p) => (
                    <tr key={`r-${p.type_id}-${p.identifiant}`}>
                      <td>{p.nom} {p.prenom}</td>
                      <td className="omv-mono">{p.type_id === "@" ? "" : p.type_id}{p.identifiant}</td>
                      <td>{p.profession}</td><td>{p.email || "—"}</td>
                      <td>{p.role === "responsable" ? <strong>Responsable</strong> : p.droit}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              </div>
            </div>
            <div className="omv-recap">
              <div><span>Adresse</span><strong className="omv-mono">{adresse}</strong></div>
              <div><span>Utilisateurs</span><strong>{personnes.length}</strong></div>
              <div><span>Annuaire santé</span><strong>{listeRouge ? "Liste rouge (non publiée)" : "Publiée"}</strong></div>
              <div><span>Messagerie</span><strong>{domaine === "dedie" ? "Incluse dans votre abonnement + option sous-domaine dédié" : "Incluse dans votre abonnement"}</strong></div>
            </div>
            {domaine === "dedie" && (
              <div className="omv-note warn">
                <strong>Option sous-domaine dédié</strong>
                <span>Sous-domaine <span className="omv-mono">{sousDomaine}.mssante.fr</span> réservé à votre structure : 1 500,00 € HT (offre de lancement, au lieu de 2 000,00 € HT), en une fois ou en 12 mensualités de 125,00 € HT. TVA 20 % en sus.</span>
                <label className="omv-check"><input type="checkbox" checked={optionOk} onChange={(e) => setOptionOk(e.target.checked)} />
                  <span>J'accepte cette option et son prix, prélevés sur le moyen de paiement de mon abonnement.</span></label>
              </div>
            )}
            <label className="omv-f">E-mail de votre abonnement OhMyVue
              <input type="email" value={emailAbo} autoComplete="email" onChange={(e) => setEmailAbo(e.target.value)} data-testid="omv-email-abo" />
              <small>Celui utilisé lors de votre souscription : il nous permet de rattacher la demande à votre abonnement. Un code de confirmation y sera envoyé.</small>
            </label>
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
              <input type="text" tabIndex={-1} autoComplete="off" value={siteWeb} onChange={(e) => setSiteWeb(e.target.value)} />
            </div>
            <label className="omv-check"><input type="checkbox" checked={habil} onChange={(e) => setHabil(e.target.checked)} data-testid="omv-habil" />
              <span>Je suis habilité·e à engager la structure et les informations saisies sont exactes.</span></label>
            <Turnstile onToken={setTsToken} />
            {envoi.etat === "erreur" && <div className="omv-note crit" role="alert">{envoi.msg}</div>}
            <div className="omv-actions">
              <button type="button" className="omv-ghost" onClick={() => aller(3)}>Retour</button>
              <button type="button" className="omv-cta" disabled={!peutValider} onClick={envoyerCode} data-testid="omv-send-code">
                {envoi.etat === "encours" ? "Vérification en cours…" : "Recevoir mon code de confirmation"}</button>
            </div>
          </section>
        )}

        {/* ============ CODE ============ */}
        {etape === 5 && jeton && (
          <section className="omv-screen">
            <div className="omv-head">
              <h1>Confirmez votre e-mail</h1>
              <p>Nous avons envoyé un code à 6 chiffres à <strong>{jeton.email_masque}</strong>. Tant qu'il n'est pas saisi, rien n'est enregistré.</p>
            </div>
            {jeton.structure_a_verifier && <div className="omv-note warn">L'Annuaire santé ne répondait pas : votre structure sera vérifiée par nos équipes avant la création de la boîte.</div>}
            <label className="omv-f omv-code">Code de confirmation
              <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} data-testid="omv-code" autoFocus
                disabled={expire}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(v);
                  if (v.length === 6) confirmer(v);
                }} />
            </label>
            <p className={`omv-hint${expire ? " err" : ""}`} aria-live="polite">
              {expire ? "Ce code a expiré : demandez-en un nouveau." : `Code valable encore ${fmtDuree(resteCode)}.`}
            </p>
            <p className="omv-hint">Rien reçu ? Vérifiez vos courriers indésirables, puis{" "}
              {resteRenvoi > 0 && !expire
                ? <span>renvoyez le code dans {resteRenvoi} s</span>
                : <button type="button" className="omv-link" onClick={renvoyer}>renvoyez le code</button>}.</p>
            {envoi.msg && <div className={`omv-note ${envoi.etat === "erreur" ? "crit" : "info"}`} role="status">{envoi.msg}</div>}
            <div className="omv-actions">
              <button type="button" className="omv-ghost" onClick={() => setQuitterCode(true)}>Modifier ma demande</button>
              <button type="button" className="omv-cta" disabled={code.length !== 6 || envoi.etat === "encours" || expire} onClick={() => confirmer()} data-testid="omv-confirm">
                {envoi.etat === "encours" ? "Confirmation…" : "Confirmer ma demande"}</button>
            </div>
            {quitterCode && (
              <div className="omv-note warn" role="alert">
                <span>Revenir à la validation annule ce code : il faudra en demander un nouveau.</span>
                <span>
                  <button type="button" className="omv-link" onClick={() => { setJeton(null); setQuitterCode(false); setEnvoi({ etat: "", msg: "" }); setEtape(4); }}>Oui, modifier ma demande</button>{" "}
                  <button type="button" className="omv-link" onClick={() => setQuitterCode(false)}>Rester ici</button>
                </span>
              </div>
            )}
          </section>
        )}

        {/* ============ CONFIRMATION ============ */}
        {etape === 6 && resultat && (
          <section className="omv-screen">
            <span className="omv-chip ok" style={{ alignSelf: "flex-start" }}>Demande reçue</span>
            <h1>Votre demande <span className="omv-mono">{resultat.reference}</span> est enregistrée</h1>
            <p className="omv-lead">Nous créons la boîte <strong className="omv-mono">{resultat.adresse}</strong> et vous prévenons dès qu'elle est publiée dans l'Annuaire santé. Un récapitulatif vient de vous être envoyé.</p>
            <ul className="omv-timeline">
              <li className="on">Demande confirmée</li>
              <li className={resultat.statut === "recue" ? "on" : ""}>Structure vérifiée dans l'Annuaire santé</li>
              <li>Création de la boîte et des accès</li>
              <li>Publication dans l'Annuaire santé</li>
            </ul>
            <div className="omv-actions">
              <button type="button" className="omv-ghost" onClick={() => window.print()}>Imprimer ce récapitulatif</button>
              <button type="button" className="omv-cta" onClick={() => {
                setResultat(null); setJeton(null); setEg(null); setPersonnes([]); setLocal(""); setDescription("");
                setHabil(false); setOptionOk(false); setCode(""); setDispo({}); setMaxEtape(2); setEtape(1);
                window.scrollTo({ top: 0 });
              }}>Commander une boîte pour un autre établissement</button>
            </div>
          </section>
        )}
      </main>

      {modale?.type === "ej" && <ModaleEj onClose={() => setModale(null)} onCreate={(s) => { setEj(s); setEg(null); setSousOnglet("eg"); setModale({ type: "eg" }); }} />}
      {modale?.type === "eg" && ej && <ModaleEg ej={ej} onClose={() => setModale(null)} onCreate={(s) => { setEg(s); setModale(null); }} />}
      {modale?.type === "personne" && (
        <ModalePersonne role={modale.role} valeur={modale.valeur} existants={personnes} index={modale.index}
          emailDefaut={emailAbo} onClose={() => setModale(null)} onSave={(p) => enregistrerPersonne(p, modale.index)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ professionnels de l'annuaire */
const simplifier = (x) => String(x || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/\b(d'|de |du |des )/g, "").replace(/[^a-z]/g, "");
const PROFESSION_ALIAS = { opticien: "Opticien-lunetier", infirmierdiplomeetat: "Infirmier", ide: "Infirmier",
  manipulateurelectroradiologiemedicale: "Manipulateur d'ERM", kinesitherapeute: "Masseur-kinésithérapeute",
  dentiste: "Chirurgien-dentiste", podologue: "Pédicure-podologue" };
const professionConnue = (x) => {
  if (!x) return "";
  const k = simplifier(x);
  const t = LISTE_PROFESSIONS.find((l) => simplifier(l) === k) || PROFESSION_ALIAS[k];
  return t || x;
};

function ProfessionnelsAnnuaire({ eg, personnes, aResp, placesLibres, onRattacher }) {
  const [etat, setEtat] = useState({ etat: "encours", items: [] });
  const [coches, setCoches] = useState({});
  const [respChoisi, setRespChoisi] = useState("");
  const [ouvert, setOuvert] = useState(true);
  const [essai, setEssai] = useState(0);
  const [cherche, setCherche] = useState("");
  const ids = (eg.fhir_ids || []).join(",");

  useEffect(() => {
    let vivant = true;
    setEtat({ etat: "encours", items: [] });
    const charger = (reste) => axios.get(`${A}/annuaire/etablissement-professionnels`, { params: { ids }, timeout: 30000 })
      .then(({ data }) => { if (vivant) setEtat({ etat: "ok", items: data.professionnels || [], tronque: data.tronque }); })
      .catch((ex) => {
        if (!vivant) return;
        if (reste > 0) { setTimeout(() => vivant && charger(reste - 1), 1500); return; }
        setEtat({ etat: ex?.response?.status === 503 ? "indisponible" : "erreur", items: [] });
      });
    charger(1);
    return () => { vivant = false; };
  }, [ids, essai]);

  const deja = new Set(personnes.filter((x) => x.type_id === "8").map((x) => x.identifiant));
  const motif = simplifier(cherche);
  const visibles = motif
    ? etat.items.filter((x) => simplifier(`${x.nom} ${x.prenom} ${x.profession || ""} ${x.rpps}`).includes(motif))
    : etat.items;
  const dispo = visibles.filter((x) => !deja.has(x.rpps));
  const choisis = dispo.filter((x) => coches[x.rpps]);
  const choisisCollab = choisis.filter((x) => aResp || x.rpps !== respChoisi);
  const depasse = choisisCollab.length > placesLibres;
  const basculer = (rpps) => {
    if (coches[rpps] && rpps === respChoisi) setRespChoisi("");
    setCoches((c) => ({ ...c, [rpps]: !c[rpps] }));
  };
  const toutCocher = () => {
    const tous = dispo.length > 0 && dispo.every((x) => coches[x.rpps]);
    if (tous) setRespChoisi("");
    setCoches(tous ? {} : Object.fromEntries(dispo.map((x) => [x.rpps, true])));
  };
  const valider = () => {
    onRattacher(choisis, aResp || !coches[respChoisi] ? "" : respChoisi);
    setCoches({}); setRespChoisi("");
  };

  if (etat.etat === "ok" && etat.items.length === 0) return null;
  return (
    <div className="omv-annu" data-testid="omv-annuaire-pros">
      <div className="omv-annu-head">
        <div>
          <strong>Professionnels de cet établissement dans l'Annuaire santé</strong>
          <p>
            {etat.etat === "encours" && "Recherche des professionnels rattachés…"}
            {etat.etat === "ok" && `${etat.items.length} professionnel${etat.items.length > 1 ? "s" : ""} trouvé${etat.items.length > 1 ? "s" : ""} pour ${eg.nom || "l'établissement"}. Cochez ceux à rattacher à la boîte : rien n'est ajouté sans votre validation.`}
            {etat.etat === "indisponible" && "L'Annuaire santé ne répond pas pour le moment. Réessayez, ou ajoutez les utilisateurs un par un."}
            {etat.etat === "erreur" && "La liste des professionnels n'a pas pu être chargée. Réessayez, ou ajoutez les utilisateurs un par un."}
          </p>
          {(etat.etat === "indisponible" || etat.etat === "erreur") && (
            <button type="button" className="omv-link" onClick={() => setEssai((n) => n + 1)} data-testid="omv-annu-retry">Réessayer</button>
          )}
        </div>
        {etat.items.length > 0 && <button type="button" className="omv-link" aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>{ouvert ? "Masquer" : "Afficher"}</button>}
      </div>
      {ouvert && etat.items.length > 0 && (
        <>
          {etat.items.length > 12 && (
            <label className="omv-f omv-annu-search">
              <span className="sr-only">Rechercher un professionnel</span>
              <input type="search" id="omv-annu-cherche" value={cherche} onChange={(e) => setCherche(e.target.value)}
                placeholder="Rechercher par nom, profession ou RPPS" data-testid="omv-annu-search" />
            </label>
          )}
          <div className="omv-tbl">
            <table>
              <thead><tr>
                <th><input type="checkbox" aria-label="Tout cocher" checked={dispo.length > 0 && dispo.every((x) => coches[x.rpps])} disabled={!dispo.length} onChange={toutCocher} /></th>
                <th>Nom</th><th>RPPS</th><th>Profession</th><th>Exercice</th><th>Carte</th>{!aResp && <th>Responsable</th>}
              </tr></thead>
              <tbody>
                {!visibles.length && (
                  <tr><td colSpan={aResp ? 6 : 7} className="omv-muted">Aucun professionnel ne correspond à « {cherche} ».</td></tr>
                )}
                {visibles.map((x) => {
                  const lie = deja.has(x.rpps);
                  const c = CARTE[x.carte] || CARTE.inconnue;
                  return (
                    <tr key={x.rpps} className={lie ? "omv-annu-lie" : ""} onClick={() => !lie && basculer(x.rpps)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        {lie ? <span className="omv-chip ok">Rattaché</span> : (
                          <input type="checkbox" id={`pro-${x.rpps}`} checked={!!coches[x.rpps]} onChange={() => basculer(x.rpps)}
                            aria-label={`Rattacher ${x.prenom} ${x.nom}`} />
                        )}
                      </td>
                      <td>{x.nom} {x.prenom}</td>
                      <td className="omv-mono">8{x.rpps}</td>
                      <td>{professionConnue(x.profession) || "—"}</td>
                      <td>{(x.fonctions && x.fonctions[0]) || x.mode_exercice || "—"}</td>
                      <td><span className={`omv-chip ${c[0]}`}>{c[1]}</span></td>
                      {!aResp && (
                        <td onClick={(e) => e.stopPropagation()}>
                          {!lie && <input type="radio" name="omv-annu-resp" checked={respChoisi === x.rpps}
                            aria-label={`Désigner ${x.prenom} ${x.nom} responsable`}
                            onChange={() => { setRespChoisi(x.rpps); setCoches((cc) => ({ ...cc, [x.rpps]: true })); }} />}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="omv-annu-foot">
            <span>
              {choisis.length} sélectionné{choisis.length > 1 ? "s" : ""}
              {!aResp && !respChoisi && choisis.length > 0 && " · désignez le responsable (bouton rond) ou ajoutez-le à la main"}
              {depasse && ` · ${placesLibres} place${placesLibres > 1 ? "s" : ""} de collaborateur restante${placesLibres > 1 ? "s" : ""}`}
              {etat.tronque && " · seuls les 100 premiers professionnels de l'annuaire sont proposés : ajoutez les autres à la main"}
            </span>
            <BtnFleche data-testid="omv-annu-ok" disabled={!choisis.length || depasse || (!aResp && !respChoisi)} onClick={valider}>
              Rattacher la sélection
            </BtnFleche>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Turnstile */
function Turnstile({ onToken }) {
  const ref = useRef(null);
  const [etat, setEtat] = useState(TURNSTILE_SITE_KEY ? "loading" : "off");
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    let id = null;
    const render = () => {
      if (!window.turnstile || !ref.current) return;
      try {
        id = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY, theme: "light",
          callback: (t) => { onToken(t); setEtat("ready"); },
          "error-callback": () => { onToken(""); setEtat("error"); },
          "expired-callback": () => onToken(""),
        });
        setEtat("ready");
      } catch (e) { setEtat("error"); }
    };
    if (window.turnstile) render();
    else {
      let s = document.getElementById("cf-turnstile-script");
      if (!s) {
        s = document.createElement("script");
        s.id = "cf-turnstile-script";
        s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
        s.async = true; s.defer = true;
        document.head.appendChild(s);
      }
      s.addEventListener("load", render);
    }
    const t = setTimeout(() => setEtat((x) => (x === "loading" ? "error" : x)), 10000);
    return () => { clearTimeout(t); try { if (id && window.turnstile) window.turnstile.remove(id); } catch (e) { /* ignore */ } };
  }, [onToken]);
  if (etat === "off") return null;
  return (
    <div>
      <div ref={ref} />
      {etat === "error" && <div className="omv-note warn">Le contrôle anti-robot n'a pas pu se charger (réseau d'entreprise ou extension de confidentialité ?). <button type="button" className="omv-link" onClick={() => window.location.reload()}>Recharger la page</button></div>}
    </div>
  );
}

/* ------------------------------------------------------------------ modale entité juridique */
function ModaleEj({ onClose, onCreate }) {
  const [type, setType] = useState("SIREN");
  const [id, setId] = useState("");
  const [res, setRes] = useState({ etat: "vide" });
  const [nomManuel, setNomManuel] = useState("");
  const [verif, setVerif] = useState({ ouvert: false, email: "", envoye: false, msg: "" });
  const [tok, setTok] = useState("");
  const v = id.replace(/\s/g, "").toUpperCase();

  useEffect(() => {
    setRes({ etat: "vide" });
    if (v.length !== 9) return undefined;
    if (type === "SIREN" && !/^\d{9}$/.test(v)) { setRes({ etat: "format" }); return undefined; }
    if (SIRENS_GENERIQUES.includes(v)) { setRes({ etat: "generique" }); return undefined; }
    setRes({ etat: "encours" });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${A}/annuaire/entite-juridique`, { params: { type, id: v } });
        setRes({ etat: data.ouverte ? "ok" : "fermee", s: data });
      } catch (ex) {
        const st = ex?.response?.status;
        setRes({ etat: st === 404 ? "introuvable" : st === 503 ? "indisponible" : "erreur", msg: detail(ex) });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [type, v]);

  const creer = () => {
    if (res.etat === "ok") onCreate({ ...res.s, verifiee: true });
    else if (res.etat === "indisponible") onCreate({ type_id: type, id: v, nom: nomManuel.trim(), fhir_ids: [], verifiee: false, ouverte: true });
  };
  const peutCreer = res.etat === "ok" || (res.etat === "indisponible" && nomManuel.trim().length >= 2);
  const envoyerVerif = async () => {
    try {
      await axios.post(`${A}/verification`, { nom: nomManuel, type_id: type, identifiant: v, email: verif.email, turnstile_token: tok });
      setVerif((x) => ({ ...x, envoye: true, msg: "" }));
    } catch (ex) { setVerif((x) => ({ ...x, msg: detail(ex) })); }
  };

  return (
    <Modale titre="Ajouter une entité juridique" tag="Informations générales" onClose={onClose}
      pied={<><button type="button" className="omv-link" onClick={onClose}>Annuler</button><BtnFleche disabled={!peutCreer} onClick={creer} data-testid="omv-ej-create">Créer</BtnFleche></>}>
      <label className="omv-mf">Type d'identifiant
        <select value={type} onChange={(e) => setType(e.target.value)}><option>SIREN</option><option>FINESS</option></select>
      </label>
      <label className="omv-mf">{type === "SIREN" ? "SIREN juridique" : "FINESS juridique"}
        <input value={id} inputMode={type === "SIREN" ? "numeric" : "text"} maxLength={11} data-testid="omv-ej-id"
          placeholder={type === "SIREN" ? "Numéro SIREN juridique" : "Numéro FINESS juridique"} onChange={(e) => setId(e.target.value)} autoFocus />
        <span className="omv-help">{type === "SIREN" ? "Le N° SIREN est composé de 9 chiffres." : "Le N° FINESS est composé de 9 caractères."}</span>
      </label>
      <div className="omv-full">
        {res.etat === "encours" && <p className="omv-hint">Recherche dans l'Annuaire santé…</p>}
        {res.etat === "format" && <p className="omv-err">Le SIREN comporte 9 chiffres.</p>}
        {res.etat === "generique" && <div className="omv-block">Ce numéro est un identifiant générique : saisissez le numéro propre à votre structure.</div>}
        {(res.etat === "ok" || res.etat === "fermee") && (
          <>
            <span className="omv-src">Trouvée dans l'Annuaire santé</span>
            <div className="omv-as-list"><CarteAnnuaire s={res.s} typeLabel="Entité juridique (EJ)" sel={res.etat === "ok"} /></div>
            {res.etat === "fermee" && <div className="omv-block">Structure fermée : elle ne peut pas porter de boîte.</div>}
          </>
        )}
        {res.etat === "introuvable" && (
          <>
            <div className="omv-found">Organisation non trouvée dans l'annuaire santé.</div>
            <div className="omv-block">
              <span><strong>Création impossible.</strong> Une boîte rattachée à une structure absente de l'Annuaire santé ne serait pas publiée. Vérifiez le numéro sur annuaire.sante.fr.</span>
              {!verif.ouvert && <button type="button" className="omv-link" onClick={() => setVerif((x) => ({ ...x, ouvert: true }))}>Demander une vérification à OhMyVue</button>}
              {verif.ouvert && !verif.envoye && (
                <div className="omv-verif">
                  <input placeholder="Nom de votre structure" value={nomManuel} onChange={(e) => setNomManuel(e.target.value)} />
                  <input type="email" placeholder="Votre e-mail" value={verif.email} onChange={(e) => setVerif((x) => ({ ...x, email: e.target.value }))} />
                  <Turnstile onToken={setTok} />
                  <button type="button" className="omv-cta" disabled={!RE_EMAIL.test(verif.email)} onClick={envoyerVerif}>Envoyer la demande</button>
                  {verif.msg && <span className="omv-err">{verif.msg}</span>}
                </div>
              )}
              {verif.envoye && <strong>Demande envoyée : nous revenons vers vous sous 48 h.</strong>}
            </div>
          </>
        )}
        {res.etat === "indisponible" && (
          <div className="omv-note warn">
            <span>L'Annuaire santé ne répond pas pour le moment. Vous pouvez continuer : votre structure sera vérifiée par nos équipes.</span>
            <input placeholder="Nom de l'entité juridique" value={nomManuel} onChange={(e) => setNomManuel(e.target.value)} />
          </div>
        )}
        {res.etat === "erreur" && <p className="omv-err">{res.msg}</p>}
      </div>
      <label className="omv-mf omv-full">Nom
        <input readOnly value={res.s?.nom || nomManuel} placeholder="Repris de l'Annuaire santé" />
      </label>
    </Modale>
  );
}

/* ------------------------------------------------------------------ modale établissement */
function ModaleEg({ ej, onClose, onCreate }) {
  const [liste, setListe] = useState({ etat: "encours", items: [] });
  const [type, setType] = useState("SIRET");
  const [id, setId] = useState("");
  const [choix, setChoix] = useState(null);
  const [recherche, setRecherche] = useState({ etat: "vide" });
  const [manuel, setManuel] = useState({ nom: "", adresse: "" });
  const v = id.replace(/\s/g, "").toUpperCase();

  useEffect(() => {
    if (!ej.fhir_ids?.length) { setListe({ etat: "indisponible", items: [] }); return; }
    axios.get(`${A}/annuaire/etablissements`, { params: { ej: ej.fhir_ids.join(",") } })
      .then(({ data }) => setListe({ etat: "ok", items: data.etablissements }))
      .catch((ex) => setListe({ etat: ex?.response?.status === 503 ? "indisponible" : "erreur", items: [] }));
  }, [ej]);

  useEffect(() => {
    setRecherche({ etat: "vide" });
    const n = type === "SIRET" ? 14 : 9;
    if (v.length !== n) return undefined;
    const local = liste.items.find((s) => s.id === v);
    if (local) { setChoix(local); return undefined; }
    setRecherche({ etat: "encours" });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${A}/annuaire/etablissement`, { params: { type, id: v } });
        const rattache = !data.entite_juridique || data.entite_juridique.id === ej.id
          || (data.entite_juridique.fhir_ids || []).some((x) => (ej.fhir_ids || []).includes(x));
        if (!rattache) setRecherche({ etat: "autre_ej" });
        else { setChoix(data.etablissement); setRecherche({ etat: "ok" }); }
      } catch (ex) {
        const st = ex?.response?.status;
        setRecherche({ etat: st === 404 ? "introuvable" : st === 503 ? "indisponible" : "erreur", msg: detail(ex) });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [type, v, liste.items, ej]);

  const choisir = (s) => { setChoix(s); setType(s.type_id === "FINESS" ? "FINESS" : "SIRET"); setId(s.id); };
  const bloque = choix && (!choix.ouverte || choix.deja_boite || choix.demande_en_cours);
  const indispo = liste.etat === "indisponible" || recherche.etat === "indisponible";
  const peutCreer = (choix && !bloque && choix.id === v) || (indispo && v.length === (type === "SIRET" ? 14 : 9) && manuel.nom.trim().length >= 2);
  const creer = () => {
    if (choix && !bloque && choix.id === v) onCreate({ ...choix, verifiee: true });
    else onCreate({ type_id: type, id: v, nom: manuel.nom.trim(), adresse: manuel.adresse.trim(), fhir_ids: [], verifiee: false, ouverte: true });
  };
  const raison = choix && (!choix.ouverte ? "Structure fermée : choisissez un établissement ouvert."
    : choix.deja_boite ? `Cet établissement a déjà une boîte organisationnelle (${(choix.boites_nos_domaines || []).join(", ")}) : une seule boîte par établissement.`
      : choix.demande_en_cours ? "Une demande de création est déjà en cours pour cet établissement." : "");

  return (
    <Modale titre="Ajouter un établissement" tag="Informations générales" onClose={onClose}
      pied={<><button type="button" className="omv-link" onClick={onClose}>Annuler</button><BtnFleche disabled={!peutCreer} onClick={creer} data-testid="omv-eg-create">Créer</BtnFleche></>}>
      <label className="omv-mf">Entité juridique<select disabled><option>{ej.nom}</option></select></label>
      <span />
      <label className="omv-mf">Type d'identifiant
        <select value={type} onChange={(e) => { setType(e.target.value); setChoix(null); }}><option>SIRET</option><option>FINESS</option></select>
      </label>
      <label className="omv-mf">Identifiant
        <input value={id} onChange={(e) => { setId(e.target.value); setChoix(null); }} placeholder="Identifiant de l'établissement" data-testid="omv-eg-id" />
        <span className="omv-help">{type === "SIRET" ? "Le N° SIRET est composé de 14 chiffres." : "Le N° FINESS est composé de 9 caractères."}</span>
      </label>
      <div className="omv-full">
        {raison && <p className="omv-err">{raison}</p>}
        {recherche.etat === "encours" && <p className="omv-hint">Recherche dans l'Annuaire santé…</p>}
        {recherche.etat === "introuvable" && <p className="omv-err">Établissement non trouvé dans l'annuaire santé.</p>}
        {recherche.etat === "autre_ej" && <p className="omv-err">Cet établissement n'est pas rattaché à {ej.nom}.</p>}
        {recherche.etat === "erreur" && <p className="omv-err">{recherche.msg}</p>}
        <span className="omv-src">Établissements de cette entité dans l'Annuaire santé</span>
        {liste.etat === "encours" && <p className="omv-hint">Chargement…</p>}
        {liste.etat === "ok" && liste.items.length === 0 && <div className="omv-block">Aucun établissement trouvé pour cette entité. Saisissez son SIRET ou son FINESS.</div>}
        {indispo && (
          <div className="omv-note warn">
            <span>L'Annuaire santé ne répond pas : saisissez l'identifiant, le nom et l'adresse de l'établissement. Nos équipes vérifieront avant la création.</span>
            <input placeholder="Nom de l'établissement" value={manuel.nom} onChange={(e) => setManuel((m) => ({ ...m, nom: e.target.value }))} />
            <input placeholder="Adresse" value={manuel.adresse} onChange={(e) => setManuel((m) => ({ ...m, adresse: e.target.value }))} />
          </div>
        )}
        <div className="omv-as-list">
          {liste.items.map((s) => {
            const pris = s.deja_boite || s.demande_en_cours;
            const sel = choix?.id === s.id;
            return (
              <CarteAnnuaire key={s.id} s={s} sel={sel} typeLabel="Entité géographique (EG)"
                action={pris ? <span className="omv-chip crit">{s.deja_boite ? "A déjà une boîte" : "Demande déjà en cours"}</span>
                  : <BtnFleche disabled={!s.ouverte} onClick={() => choisir(s)}>{sel ? "Sélectionné" : "Choisir"}</BtnFleche>} />
            );
          })}
        </div>
      </div>
      <label className="omv-mf omv-full">Nom
        <input readOnly value={choix?.nom || manuel.nom} placeholder="Repris de l'Annuaire santé" />
      </label>
    </Modale>
  );
}

/* ------------------------------------------------------------------ modale personne */
function ModalePersonne({ role, valeur, existants, index, emailDefaut, onClose, onSave }) {
  const [p, setP] = useState(valeur || {
    role, nom: "", prenom: "", type_id: "8", identifiant: "", email: role === "responsable" ? emailDefaut || "" : "",
    telephone: "", profession: "", droit: role === "responsable" ? "Responsable" : "Délégataire", carte: "", fichier: null,
  });
  const [rpps, setRpps] = useState({ etat: valeur?.carte && valeur.type_id === "8" ? "ok" : "vide" });
  const [err, setErr] = useState("");
  const set = (k, x) => setP((s) => ({ ...s, [k]: x }));
  const fmt = FORMATS[p.type_id];
  const aide = AIDE[p.type_id];
  const verrou = p.type_id === "8" && rpps.etat === "ok";

  useEffect(() => {
    if (p.type_id !== "8") { setRpps({ etat: "vide" }); return undefined; }
    const d = p.identifiant.replace(/\D/g, "");
    if (d.length !== 11) { setRpps({ etat: "vide" }); return undefined; }
    if (valeur && valeur.identifiant === d && valeur.carte) return undefined;
    setRpps({ etat: "encours" });
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${A}/annuaire/praticien/${d}`);
        setP((s) => ({ ...s, nom: (data.nom || s.nom || "").toUpperCase(), prenom: prenomPropre(data.prenom || s.prenom),
          profession: professionConnue(data.profession) || s.profession, carte: data.carte || "inconnue" }));
        setRpps({ etat: "ok", data });
      } catch (ex) {
        const st = ex?.response?.status;
        setRpps({ etat: st === 404 ? "introuvable" : st === 503 ? "indisponible" : "erreur" });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [p.type_id, p.identifiant, valeur]);

  const enregistrer = () => {
    const ident = p.identifiant.trim();
    if (!fmt.re.test(ident)) return setErr(`Identifiant invalide : format attendu ${p.type_id === "@" ? "" : p.type_id + " + "}${fmt.ph}.`);
    if (p.type_id === "8" && rpps.etat === "introuvable") return setErr("RPPS introuvable dans l'Annuaire santé.");
    if (p.type_id === "8" && rpps.etat === "encours") return setErr("Vérification du RPPS en cours…");
    if (p.nom.trim().length < 1 || p.prenom.trim().length < 1) return setErr("Nom et prénom sont requis.");
    if (!p.profession) return setErr("La profession est requise.");
    if (role === "responsable" && !RE_EMAIL.test(p.email)) return setErr("L'adresse e-mail du responsable est requise.");
    if (p.email && !RE_EMAIL.test(p.email)) return setErr("Adresse e-mail invalide.");
    if (p.fichier && p.fichier.size > 5 * 1024 * 1024) return setErr("La pièce justificative dépasse 5 Mo.");
    if (existants.some((x, i) => i !== index && x.type_id === p.type_id && x.identifiant.toLowerCase() === ident.toLowerCase())) {
      return setErr(role === "responsable"
        ? "Cette personne est déjà dans la liste : fermez cette fenêtre et utilisez « Nommer responsable » sur sa ligne."
        : "Cette personne est déjà dans la liste.");
    }
    const carte = p.type_id === "8" ? (p.carte || "inconnue") : ["3", "4", "5", "6", "1"].includes(p.type_id) ? "cpe" : "sans";
    return onSave({ ...p, identifiant: ident, nom: p.nom.trim().toUpperCase(), prenom: prenomPropre(p.prenom), carte, pieceNom: p.fichier?.name || "" });
  };

  return (
    <Modale titre={role === "responsable" ? "Création du responsable de la structure" : "Ajouter un utilisateur"}
      tag={role === "responsable" ? "(obligatoire)" : "Informations générales"} onClose={onClose}
      pied={<><button type="button" className="omv-link" onClick={onClose}>Annuler</button><BtnFleche onClick={enregistrer} data-testid="omv-p-ok">Terminer</BtnFleche></>}>
      <label className="omv-mf">Nom<input value={p.nom} readOnly={verrou} onChange={(e) => set("nom", e.target.value)} placeholder="Nom de l'utilisateur" /></label>
      <label className="omv-mf">Prénom<input value={p.prenom} readOnly={verrou} onChange={(e) => set("prenom", e.target.value)} placeholder="Prénom de l'utilisateur" /></label>
      <label className="omv-mf">Type d'ID
        <select value={p.type_id} data-testid="omv-p-type" onChange={(e) => { set("type_id", e.target.value); set("identifiant", ""); set("carte", ""); }}>
          {TYPES_ID.map((g) => <optgroup key={g.groupe} label={g.groupe}>{g.items.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}</optgroup>)}
        </select>
      </label>
      <label className="omv-mf">ID
        <span className="omv-pre"><b>{p.type_id === "@" ? "@" : p.type_id}</b>
          <input value={p.identifiant} placeholder={fmt.ph} data-testid="omv-p-id" onChange={(e) => set("identifiant", e.target.value.trim())} /></span>
        {rpps.etat === "encours" && <span className="omv-help">Recherche dans l'Annuaire santé…</span>}
        {rpps.etat === "introuvable" && <span className="omv-err">RPPS introuvable dans l'Annuaire santé.</span>}
        {rpps.etat === "indisponible" && <span className="omv-help">Annuaire santé indisponible : complétez les champs à la main.</span>}
      </label>
      <div className="omv-full">
        <span className="omv-src">Où trouver ce numéro ?</span>
        <div className={`omv-help-panel${aide.img ? "" : " noimg"}`}>
          <p><b>{aide.t}</b><br />{aide.txt}</p>
          {aide.img && <img src={aide.img} alt={`Carte : emplacement du ${aide.t}`} />}
        </div>
      </div>
      {rpps.etat === "ok" && (
        <div className="omv-full omv-note info">
          <strong>Trouvé dans l'Annuaire santé</strong>
          <span>{p.prenom} {p.nom} · {p.profession} · {(CARTE[p.carte] || CARTE.inconnue)[1]}{p.carte === "opposition" ? " : connexion par e-CPS" : ""}</span>
        </div>
      )}
      <label className="omv-mf">Adresse email<input type="email" value={p.email} onChange={(e) => set("email", e.target.value)} placeholder="Adresse email de l'utilisateur" /></label>
      <label className="omv-mf">Numéro de téléphone<input value={p.telephone} onChange={(e) => set("telephone", e.target.value)} placeholder="N° de téléphone (facultatif)" /></label>
      <label className="omv-mf omv-full">Profession
        <select value={p.profession} disabled={verrou && !!p.profession} onChange={(e) => set("profession", e.target.value)}>
          <option value="">Sélectionner une profession</option>
          {p.profession && !LISTE_PROFESSIONS.includes(p.profession) && <option>{p.profession}</option>}
          {LISTE_PROFESSIONS.map((x) => <option key={x}>{x}</option>)}
        </select>
      </label>
      {role !== "responsable" && (
        <label className="omv-mf">Droit sur la boîte
          <select value={p.droit} onChange={(e) => set("droit", e.target.value)}><option>Délégataire</option><option>Cotitulaire</option></select>
        </label>
      )}
      <label className="omv-mf omv-full">Pièce justificative (facultatif)
        <input type="file" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" capture="environment"
          onChange={(e) => set("fichier", e.target.files?.[0] || null)} />
        <span className="omv-help">Photo du recto de la carte CPS ou CPE, ou attestation de l'employeur. JPG, PNG ou PDF, 5 Mo maximum.{p.pieceNom && !p.fichier ? ` Pièce à joindre de nouveau : ${p.pieceNom}.` : ""}</span>
      </label>
      {err && <p className="omv-err omv-full" role="alert">{err}</p>}
    </Modale>
  );
}

/* ------------------------------------------------------------------ styles */
const CSS = `
.omv-page{--navy:#0F2B5B;--ink:#2F3140;--sub:#6C6A8A;--line:#E3E8F1;--bg:#EEF2F8;--blue:#1F6FB8;--blue-soft:#E4EFFA;--green:#9DBE3A;--ok:#1E7F52;--ok-bg:#E6F4EC;--warn:#9A6212;--warn-bg:#FBF1DF;--crit:#B23A2E;--crit-bg:#FBE9E7;
  min-height:100vh;background:var(--bg);color:var(--ink);font-family:"Jost","DM Sans",system-ui,sans-serif;padding-bottom:64px}
.omv-page *{box-sizing:border-box}
.omv-appbar{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;padding:16px clamp(16px,4vw,40px);background:radial-gradient(900px 340px at 88% -70%,rgba(79,195,247,.30),transparent 62%),linear-gradient(168deg,#0A1A33 0%,#102C4D 100%);border-bottom:0}
.omv-logo{display:inline-flex;align-items:center;background:#fff;border-radius:12px;padding:8px 13px;box-shadow:0 2px 10px rgba(5,15,32,.28)} .omv-logo img{display:block;height:24px;width:auto}
.omv-who{font-size:.9rem;color:rgba(220,233,247,.86)}
.omv-appbar .omv-aide{color:#fff}
.omv-flow{display:flex;gap:6px;overflow-x:auto;padding:4px clamp(16px,4vw,40px) 46px;background:linear-gradient(168deg,#102C4D 0%,#123258 100%);border-bottom:0}
.omv-flow button{border:0;background:none;display:flex;gap:8px;align-items:center;padding:6px 12px;border-radius:99px;font:500 .9rem "Jost",sans-serif;color:rgba(220,233,247,.66);cursor:pointer;white-space:nowrap}
.omv-flow button .n{width:24px;height:24px;border-radius:50%;border:1.5px solid rgba(220,233,247,.35);display:grid;place-items:center;font-size:.75rem}
.omv-flow button[aria-current="step"]{background:rgba(255,255,255,.12);color:#fff}
.omv-flow button[aria-current="step"] .n{background:#4FC3F7;border-color:#4FC3F7;color:#06243B}
.omv-flow button.done .n{border-color:#7BE0BA;color:#7BE0BA}
.omv-flow button:disabled{opacity:.5;cursor:not-allowed}
.omv-main{max-width:1080px;margin:0 auto;padding:0 clamp(16px,4vw,32px) 32px}
.omv-card1{background:#fff;border:1px solid var(--line);border-radius:20px;box-shadow:0 2px 4px rgba(10,26,51,.05),0 24px 48px -28px rgba(10,26,51,.45);padding:clamp(18px,3vw,28px);margin-top:-30px}
.omv-trust{display:flex;flex-wrap:wrap;gap:8px 22px;margin:0;padding:0 0 16px;list-style:none;border-bottom:1px solid var(--line);font-size:.85rem;color:var(--sub)}
.omv-trust li{display:flex;align-items:center;gap:7px}
.omv-trust li::before{content:"";width:7px;height:7px;border-radius:50%;background:var(--blue);flex:none}
.omv-screen{display:flex;flex-direction:column;gap:18px}
.omv-head h1,.omv-screen>h1{font:600 clamp(1.4rem,3vw,1.8rem) "Jost",sans-serif;color:var(--navy);margin:0}
.omv-head p,.omv-lead{color:var(--sub);margin:6px 0 0;max-width:68ch;line-height:1.55}
.omv-typer{display:flex;flex-direction:column;gap:8px}
.omv-typer label{font-weight:600;font-size:.9rem}
.omv-big{display:flex;align-items:center;border:2px solid var(--blue);border-radius:16px;background:#fff;overflow:hidden}
.omv-big input{border:0;outline:0;font:500 1.6rem ui-monospace,Menlo,monospace;padding:14px 18px;flex:1;min-width:0;background:transparent;color:var(--ink)}
.omv-big span{font:500 1.6rem ui-monospace,Menlo,monospace;color:var(--sub);padding-right:18px}
.omv-typer small{color:var(--sub)} .omv-typer small.err{color:var(--crit)}
.omv-domsel{display:flex;flex-direction:column;gap:8px}
.omv-domsel label{font-weight:600;font-size:.9rem}
.omv-domsel select{font:400 1rem "Jost",sans-serif;padding:12px 14px;border:1px solid #D3DAE6;border-radius:12px;background:#fff;color:var(--ink);width:100%;max-width:560px}
.omv-domsel select:focus{outline:2px solid var(--blue);outline-offset:1px}
.omv-apercu{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;background:var(--blue-soft);border-radius:16px;padding:14px 16px}
.omv-dedbox{display:flex;flex-direction:column;gap:4px;border-left:3px solid var(--blue);padding-left:14px}
.omv-avant summary{cursor:pointer;font-weight:600}
.omv-avant[open] summary{margin-bottom:8px}
.omv-addrs{display:flex;flex-direction:column;background:#fff;border-radius:24px;box-shadow:0 6px 18px rgba(20,30,60,.08);overflow:hidden}
.omv-arow{display:grid;grid-template-columns:auto 1fr auto auto;gap:14px;align-items:center;padding:16px 20px;border-top:1px solid var(--line);cursor:pointer}
.omv-arow:first-child{border-top:0}
.omv-arow:has(input:checked){background:var(--blue-soft)}
.omv-arow.off{opacity:.6;cursor:not-allowed}
.omv-mail{font:500 1.05rem ui-monospace,Menlo,monospace;overflow-wrap:anywhere} .omv-mail .l{color:var(--blue)}
.omv-sub{font-size:.82rem;color:var(--sub)}
.omv-pr{font-weight:600;text-align:right;color:var(--navy);white-space:nowrap;line-height:1.3} .omv-pr em{font-style:normal;font-weight:400;color:var(--sub)}
.omv-dedin{display:flex;width:fit-content;align-items:center;gap:4px;margin-top:6px;font-family:ui-monospace,Menlo,monospace}
.omv-dedin input{font:400 .9rem ui-monospace,Menlo,monospace;padding:5px 9px;border:1px solid var(--line);border-radius:10px;width:190px}
.omv-chip{display:inline-block;font:500 .72rem "Jost",sans-serif;padding:4px 10px;border-radius:99px;white-space:nowrap}
.omv-chip.ok{background:var(--ok-bg);color:var(--ok)} .omv-chip.warn{background:var(--warn-bg);color:var(--warn)} .omv-chip.crit{background:var(--crit-bg);color:var(--crit)} .omv-chip.n{background:var(--blue-soft);color:var(--blue)}
.omv-f{display:flex;flex-direction:column;gap:6px;font-weight:600;font-size:.9rem}
.omv-f input{font:400 1rem "Jost",sans-serif;padding:11px 14px;border:1px solid #D3DAE6;border-radius:12px;background:#fff;color:var(--ink)}
.omv-f small{font-weight:400;color:var(--sub)}
.omv-code input{font:500 1.8rem ui-monospace,Menlo,monospace;letter-spacing:.4em;text-align:center;max-width:280px}
.omv-check{display:flex;gap:10px;align-items:flex-start;font-size:.95rem}
.omv-check input{margin-top:5px;accent-color:var(--blue);width:16px;height:16px}
.omv-actions{display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;padding-top:14px;border-top:1px solid var(--line)}
.omv-cta{font:500 .95rem "Jost",sans-serif;border:0;border-radius:99px;padding:12px 22px;background:var(--navy);color:#fff;cursor:pointer}
.omv-cta:disabled{opacity:.45;cursor:not-allowed}
.omv-ghost{font:500 .95rem "Jost",sans-serif;border:1px solid var(--navy);border-radius:99px;padding:11px 20px;background:transparent;color:var(--navy);cursor:pointer}
.omv-link{border:0;background:none;color:var(--blue);font:600 .9rem "Jost",sans-serif;cursor:pointer;padding:4px 6px}
.omv-hint{color:var(--sub);font-size:.88rem;margin:0}
.omv-err{color:var(--crit);font-size:.85rem;margin:4px 0}
.omv-note{display:flex;flex-direction:column;gap:8px;border-radius:16px;padding:12px 16px;font-size:.92rem}
.omv-note.info{background:var(--blue-soft)} .omv-note.warn{background:var(--warn-bg);color:var(--warn)} .omv-note.crit{background:var(--crit-bg);color:var(--crit)}
.omv-note input{font:400 .95rem "Jost",sans-serif;padding:9px 12px;border:1px solid #D3DAE6;border-radius:10px;color:var(--ink)}
.omv-mc{background:#dfe6f1;border-radius:30px;padding:18px}
.omv-mc-tabs{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}
.omv-mc-tabs span{border-radius:99px;padding:8px 16px;background:#fff;font-weight:500;font-size:.9rem;box-shadow:0 2px 8px rgba(20,30,60,.06)}
.omv-mc-tabs span.on{background:var(--blue);color:#fff}
.omv-panel{background:#fff;border-radius:26px;box-shadow:0 6px 18px rgba(20,30,60,.08);padding:20px 22px;display:flex;flex-direction:column;gap:12px}
.omv-row{display:flex;align-items:center;gap:14px;flex-wrap:wrap}
.omv-sub-tab{border:0;background:none;font:500 1.15rem "Jost",sans-serif;color:var(--sub);cursor:pointer;padding:4px 0;border-bottom:2px solid transparent}
.omv-sub-tab.on{color:var(--ink);border-bottom-color:var(--green)} .omv-sub-tab small{font-size:.75rem}
.omv-spacer{flex:1}
.omv-btn{display:inline-flex;align-items:center;gap:12px;background:#fff;color:#222;border:0;border-radius:99px;box-shadow:0 4px 12px rgba(20,30,60,.12);font:500 .9rem "Jost",sans-serif;padding:0 0 0 18px;cursor:pointer;min-height:38px}
.omv-btn i{width:38px;height:38px;border-radius:50%;background:var(--blue);color:#fff;display:grid;place-items:center;font-style:normal}
.omv-btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none}
.omv-guide{background:var(--blue-soft);border-radius:99px;padding:10px 16px;font-size:.92rem} .omv-guide b{color:var(--blue)}
.omv-tbl{overflow-x:auto}
.omv-tbl table{border-collapse:collapse;width:100%;font-size:.92rem}
.omv-tbl th{text-align:left;font-weight:500;color:var(--sub);padding:10px 12px;border-bottom:1px solid var(--line);white-space:nowrap}
.omv-tbl td{padding:10px 12px;border-bottom:1px solid var(--line);vertical-align:middle}
.omv-annu{border:1px solid #D3DAE6;border-radius:16px;padding:14px 16px;margin:0 0 16px;background:#F7F9FD}.omv-annu-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}.omv-annu-head p{margin:4px 0 0;color:var(--sub);font-size:.9rem}.omv-annu .omv-tbl{margin-top:10px}.omv-annu tbody tr{cursor:pointer}.omv-annu tr.omv-annu-lie{cursor:default;opacity:.65}.omv-annu input[type=checkbox],.omv-annu input[type=radio]{width:18px;height:18px;accent-color:#2166C2}.omv-annu-search{margin:10px 0 0}.omv-annu-search input{width:100%}.omv-annu .omv-muted{color:var(--sub);text-align:center;padding:14px;cursor:default}.sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}.omv-annu-foot{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-top:10px;font-size:.9rem;color:var(--sub)}
.omv-avant{background:#fff;border:1px solid #D3DAE6;border-radius:16px;padding:14px 18px;font-size:.93rem}.omv-avant ul{margin:6px 0 0;padding-left:18px}.omv-avant li{margin:2px 0}.omv-aide{margin-left:18px;font-size:.9rem;color:#1F6FB8;text-decoration:underline}.omv-mail .l.ph{color:#9AA6B8;font-style:italic}.omv-hint.err{color:var(--crit)}
@media print{.omv-appbar,.omv-flow,.omv-actions{display:none!important}}
.omv-empty{text-align:center;color:var(--sub);padding:30px 10px!important}
.omv-em{display:block;color:var(--sub);font-size:.82rem}
.omv-mono{font-family:ui-monospace,Menlo,monospace;font-size:.88em}
.omv-nowrap{white-space:nowrap}
.omv-select{font:400 .9rem "Jost",sans-serif;padding:7px 10px;border:1px solid #D3DAE6;border-radius:99px;background:#fff}
.omv-recap{background:#fff;border-radius:20px;box-shadow:0 6px 18px rgba(20,30,60,.06);overflow:hidden}
.omv-recap div{display:flex;justify-content:space-between;gap:12px;padding:11px 18px;border-top:1px solid var(--line);flex-wrap:wrap}
.omv-recap div:first-child{border-top:0} .omv-recap span{color:var(--sub)} .omv-recap strong{font-weight:500;text-align:right}
.omv-timeline{list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:10px}
.omv-timeline li{display:flex;gap:10px;align-items:center} .omv-timeline li::before{content:"";width:12px;height:12px;border-radius:50%;background:var(--line)}
.omv-timeline li.on::before{background:var(--ok)}
.omv-overlay{position:fixed;inset:0;background:rgba(47,49,64,.45);display:flex;align-items:flex-start;justify-content:center;padding:40px 16px;overflow:auto;z-index:50}
.omv-modal{background:#fff;border-radius:30px;width:min(780px,100%);box-shadow:0 20px 50px rgba(0,0,0,.25)}
.omv-modal header{display:flex;align-items:center;gap:12px;padding:22px 26px 6px;flex-wrap:wrap}
.omv-mi{width:38px;height:38px;border-radius:12px;background:var(--blue-soft);color:var(--blue);display:grid;place-items:center}
.omv-modal h3{font:600 1.05rem "Jost",sans-serif;text-transform:uppercase;letter-spacing:.01em;color:var(--ink);margin:0}
.omv-tag{color:var(--sub);font-size:.85rem}
.omv-x{margin-left:auto;border:0;background:none;font-size:1.6rem;cursor:pointer;color:var(--ink);line-height:1}
.omv-body{padding:14px 26px 18px;display:grid;grid-template-columns:1fr 1fr;gap:14px 22px}
.omv-full{grid-column:1/-1}
.omv-modal footer{display:flex;justify-content:flex-end;align-items:center;gap:20px;padding:16px 26px;border-top:1px solid var(--line)}
.omv-mf{display:flex;flex-direction:column;gap:6px;font-size:.88rem;font-weight:500}
.omv-mf input,.omv-mf select{font:400 .95rem "Jost",sans-serif;padding:10px 12px;border:1px solid #D3DAE6;border-radius:12px;background:#fff;color:var(--ink);width:100%}
.omv-mf input[readonly],.omv-mf select:disabled{background:#F5F7FB;color:var(--sub)}
.omv-pre{display:flex;border:1px solid #D3DAE6;border-radius:12px;overflow:hidden;background:#fff}
.omv-pre b{padding:10px;border-right:1px solid var(--line);min-width:36px;text-align:center;color:var(--blue);font-weight:600}
.omv-pre input{border:0;border-radius:0}
.omv-help{font-weight:400;color:var(--sub);font-size:.8rem}
.omv-src{display:block;font:500 .72rem "Jost",sans-serif;text-transform:uppercase;letter-spacing:.08em;color:var(--sub);margin:6px 0}
.omv-found{font:600 1rem "Jost",sans-serif;color:var(--crit);margin-bottom:6px}
.omv-block{display:flex;flex-direction:column;gap:6px;padding:10px 14px;border-radius:16px;background:var(--crit-bg);color:var(--crit);font-size:.9rem}
.omv-verif{display:flex;flex-direction:column;gap:8px;color:var(--ink)}
.omv-verif input{font:400 .95rem "Jost",sans-serif;padding:9px 12px;border:1px solid #D3DAE6;border-radius:10px}
.omv-as-list{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:14px}
.omv-as-card{background:#fff;border-radius:30px;box-shadow:0 6px 18px rgba(20,30,60,.10);padding:20px 22px 18px;display:flex;flex-direction:column;gap:14px;border:2px solid transparent}
.omv-as-card.sel{border-color:var(--blue)}
.omv-as-top{display:grid;grid-template-columns:62px 1fr;gap:14px}
.omv-as-ico{width:58px;height:58px;border-radius:12px;border:1px solid #D9DDE8;display:grid;place-items:center}
.omv-as-name{font-weight:600;text-transform:uppercase}
.omv-as-id{font-size:.88rem;font-weight:500} .omv-as-id span{color:var(--sub);font-weight:400}
.omv-as-t{font-size:.88rem;color:var(--sub);margin-top:4px}
.omv-as-bot{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap}
.omv-pill{display:inline-flex;align-items:center;background:var(--green);color:#fff;font-weight:500;font-size:.85rem;padding:9px 18px;border-radius:99px}
.omv-pill.closed{background:var(--crit)}
.omv-help-panel{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.15fr);gap:16px;align-items:center;background:var(--bg);border-radius:20px;padding:12px}
.omv-help-panel.noimg{grid-template-columns:1fr}
.omv-help-panel p{margin:0;font-size:.85rem;line-height:1.45;color:var(--blue)} .omv-help-panel b{color:var(--ink)}
.omv-help-panel img{width:100%;height:auto;border-radius:12px;background:#fff;display:block}
@media (max-width:720px){
  .omv-arow{grid-template-columns:auto 1fr}.omv-arow .omv-chip,.omv-arow .omv-pr{grid-column:2;justify-self:start;text-align:left}
  .omv-body{grid-template-columns:1fr}.omv-help-panel{grid-template-columns:1fr}
  .omv-big input,.omv-big span{font-size:1.15rem}.omv-mc{padding:10px}.omv-panel{padding:14px}
}
`;
